import { DataQualityGrade } from '@lune-climate/lune'
import { DataQualityAggregatedTimeseries } from '@lune-fe/dapi-models'
import {
    BarChart,
    BarChartData,
    getDataQualityScoreColor,
    padLeftNumber,
    padLeftString,
} from '@lune-fe/lune-components-lib'
import { LoadingWrapper } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import moment from 'moment'
import { FC, useMemo } from 'react'
import { Range } from 'react-date-range'

import ChartEmptyState from 'views/DataHealth/ChartEmptyState'
import DataHealthByShipmentLegsChartLegend from 'views/DataHealth/DataHealthByShipmentLegsChartLegend'

import 'chartjs-adapter-moment'

export const dataLabels: Record<string, string> = {
    A: 'A Excellent',
    B: 'B Good',
    C: 'C Fair',
    D: 'D Poor',
}
export const chartOrdering: Record<string, number> = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
}

const isTimeseriesAllZero = (
    timeseries: DataQualityAggregatedTimeseries['byShipmentLegs']['timeseries'],
): boolean =>
    timeseries.every((item) => item.a === 0 && item.b === 0 && item.c === 0 && item.d === 0)

const DataHealthByShipmentLegsChart: FC<{
    data: DataQualityAggregatedTimeseries['byShipmentLegs']
    dateRange: Range
    loading: boolean
}> = ({ data, dateRange, loading }) => {
    const timeseries = data.timeseries
    const isEmpty = isTimeseriesAllZero(timeseries)

    const chartData: BarChartData = useMemo(() => {
        const rangeDuration = moment.duration(moment(dateRange.endDate).diff(dateRange.startDate))
        const format =
            !dateRange.endDate || !dateRange.startDate || rangeDuration.asMonths() > 1
                ? 'MMM, YYYY'
                : 'DD MMM, YYYY'

        return {
            labels: padLeftString(
                timeseries.map((d) => {
                    return moment(d.date).format(format)
                }),
            ),
            // Order datasets A, B, C, D to match labelOrdering and legend
            datasets: [
                {
                    data: padLeftNumber(timeseries.map((d) => d.a)),
                    backgroundColor: getDataQualityScoreColor(DataQualityGrade.A),
                    barThickness: 12,
                },
                {
                    data: padLeftNumber(timeseries.map((d) => d.b)),
                    backgroundColor: getDataQualityScoreColor(DataQualityGrade.B),
                    barThickness: 12,
                },
                {
                    data: padLeftNumber(timeseries.map((d) => d.c)),
                    backgroundColor: getDataQualityScoreColor(DataQualityGrade.C),
                    barThickness: 12,
                },
                {
                    data: padLeftNumber(timeseries.map((d) => d.d)),
                    backgroundColor: getDataQualityScoreColor(DataQualityGrade.D),
                    barThickness: 12,
                },
            ],
        }
    }, [data, dateRange])

    return (
        <Box sx={{ mt: 4 }}>
            {!isEmpty ? (
                <>
                    <DataHealthByShipmentLegsChartLegend />
                    <LoadingWrapper loading={loading} sx={{ height: '431px', width: '100%' }}>
                        <BarChart
                            label={'Legs'}
                            chartData={chartData}
                            dataLabels={dataLabels}
                            showTooltipLabels={false}
                            labelOrdering={(a: string, b: string) =>
                                chartOrdering[a] - chartOrdering[b]
                            }
                        />
                    </LoadingWrapper>
                </>
            ) : (
                <ChartEmptyState />
            )}
        </Box>
    )
}

export default DataHealthByShipmentLegsChart
